'use client'

import { useRef, useEffect, useCallback } from 'react'
import { useTranslations } from 'next-intl'
import gsap from 'gsap'
import type { Locale } from './useLocale'

interface Props {
  locale: Locale
  onChange: (next: Locale) => void
  isDark: boolean
  size?: 'md' | 'lg'
}

const OLIVE = '#5B7005'
const LIME  = '#9FAF08'
const CREAM = '#F5ECD8'
const EARTH = '#513205'

export default function LangToggle({ locale, onChange, isDark, size = 'md' }: Props) {
  const sliderRef = useRef<HTMLDivElement>(null)
  const t = useTranslations('nav')

  const btnW = size === 'lg' ? 48 : 32
  const btnH = size === 'lg' ? 40 : 24
  const pad  = size === 'lg' ? 5 : 4
  const font = size === 'lg' ? 14 : 12
  const containerH = btnH + pad * 2

  useEffect(() => {
    if (!sliderRef.current) return
    const targetX = locale === 'es' ? 0 : btnW
    const overshoot = targetX + (locale === 'es' ? -2 : 2)
    gsap.to(sliderRef.current, {
      x: overshoot,
      duration: 0.22,
      ease: 'power2.out',
      overwrite: true,
      onComplete: () => {
        gsap.to(sliderRef.current, {
          x: targetX, duration: 0.12, ease: 'power2.inOut',
        })
      },
    })
  }, [locale, btnW])

  const handle = useCallback((next: Locale) => {
    if (next !== locale) onChange(next)
  }, [locale, onChange])

  const sliderBg    = isDark ? LIME : OLIVE
  const borderCol   = isDark ? 'rgba(245,238,217,0.2)' : 'rgba(91,112,5,0.2)'
  const activeText  = isDark ? EARTH : CREAM
  const inactiveTxt = isDark ? 'rgba(245,238,217,0.55)' : 'rgba(81,50,5,0.55)'

  return (
    <div
      role="group"
      aria-label={t('langAria')}
      className="lang-toggle"
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        padding: pad,
        height: containerH,
        borderRadius: 999,
        border: `1px solid ${borderCol}`,
        background: 'transparent',
        transition: 'border-color 300ms ease, transform 200ms ease, box-shadow 200ms ease',
      }}
    >
      <div
        ref={sliderRef}
        aria-hidden="true"
        style={{
          position: 'absolute',
          left: pad,
          top: pad,
          width: btnW,
          height: btnH,
          borderRadius: 999,
          background: sliderBg,
          transition: 'background 300ms ease',
          pointerEvents: 'none',
          willChange: 'transform',
        }}
      />

      {(['es', 'en'] as Locale[]).map(l => {
        const isActive = l === locale
        return (
          <button
            key={l}
            type="button"
            onClick={() => handle(l)}
            aria-pressed={isActive}
            aria-label={l === 'es' ? t('langEs') : t('langEn')}
            style={{
              position: 'relative',
              zIndex: 1,
              width: btnW,
              height: btnH,
              background: 'none',
              border: 'none',
              cursor: isActive ? 'default' : 'pointer',
              fontFamily: 'var(--font-body)',
              fontWeight: 500,
              fontSize: font,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              color: isActive ? activeText : inactiveTxt,
              transition: 'color 250ms ease',
              borderRadius: 999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {l.toUpperCase()}
          </button>
        )
      })}
    </div>
  )
}
